import { useState } from 'react';

const PAGES = [
  'Page d’accueil',
  'Catalogue & fiche produit',
  'Panier & checkout',
  'Réservations (calendrier)',
  'Formulaires & contact',
  'Pages du thème & pages personnalisées',
];

interface Props {
  pixelId: string;
  enabled: boolean;
}

/** Mode d'emploi + couverture du pixel sur le site public. */
export default function InstallGuide({ pixelId, enabled }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!pixelId) return;
    try {
      await navigator.clipboard.writeText(pixelId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const steps = [
    {
      icon: 'ri-facebook-circle-line',
      title: 'Récupérez votre Pixel ID',
      text: 'Dans Meta Events Manager → Sources de données → votre pixel. L’identifiant fait 15 ou 16 chiffres.',
    },
    {
      icon: 'ri-save-3-line',
      title: 'Collez-le et enregistrez',
      text: 'Le pixel est injecté automatiquement dans le <head> de votre site public, sans modifier votre thème.',
    },
    {
      icon: 'ri-radar-line',
      title: 'Activez et vérifiez',
      text: 'Activez l’interrupteur puis utilisez « Tester le pixel » : un événement PageView de test part vers Meta.',
    },
  ];

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-book-open-line text-primary-500"></i>
          Installation en 3 étapes
        </h3>
        <div className="mt-3 space-y-3">
          {steps.map((step, index) => (
            <div key={step.title} className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-800">{step.title}</p>
                <p className="text-xs text-foreground-500 leading-relaxed">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pixel ID à copier */}
      <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-400 mb-1.5">
          Pixel ID enregistré
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate text-sm font-mono text-foreground-900">
            {pixelId || 'Aucun pixel configuré'}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!pixelId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/70 text-xs font-medium text-foreground-600 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className={copied ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'}></i>
            {copied ? 'Copié' : 'Copier'}
          </button>
        </div>
      </div>

      {/* Couverture */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-800 mb-1">Où le pixel est-il présent ?</h4>
        <p className="text-xs text-foreground-500 mb-2.5">
          {enabled
            ? 'Sur toutes les pages publiques de votre boutique, y compris celles de votre thème.'
            : 'Dès que vous activez le pixel, il couvre les pages suivantes.'}
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {PAGES.map((page) => (
            <li key={page} className="flex items-center gap-2 text-xs text-foreground-600">
              <i className={`${enabled ? 'ri-checkbox-circle-fill text-accent-500' : 'ri-time-line text-foreground-300'} text-sm flex-shrink-0`}></i>
              <span className="truncate">{page}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-start gap-2 rounded-md bg-secondary-50 px-3 py-2.5">
        <i className="ri-information-line text-secondary-600 text-sm flex-shrink-0 mt-0.5"></i>
        <p className="text-[11px] text-foreground-600 leading-relaxed">
          Astuce : si aucun événement n’apparaît sous 24 h, vérifiez qu’un bloqueur de publicité
          (AdBlock) n’est pas actif sur votre navigateur, puis relancez le test.
        </p>
      </div>
    </div>
  );
}